/** Small math / 2D vector helpers used across systems. */

export interface Vec2 {
  x: number;
  y: number;
}

export const clamp = (v: number, min: number, max: number): number =>
  v < min ? min : v > max ? max : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Frame-rate independent smoothing factor for exponential lerp. */
export const damp = (a: number, b: number, lambda: number, dt: number): number =>
  lerp(a, b, 1 - Math.exp(-lambda * dt));

export const len = (x: number, y: number): number => Math.hypot(x, y);

export const dist = (ax: number, ay: number, bx: number, by: number): number =>
  Math.hypot(bx - ax, by - ay);

export const distSq = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  return dx * dx + dy * dy;
};

export const angleTo = (ax: number, ay: number, bx: number, by: number): number =>
  Math.atan2(by - ay, bx - ax);

/** Returns a normalized {x,y} with the given length, or {0,0} if degenerate. */
export const normalize = (x: number, y: number): Vec2 => {
  const l = Math.hypot(x, y);
  if (l < 1e-6) return { x: 0, y: 0 };
  return { x: x / l, y: y / l };
};

export const TWO_PI = Math.PI * 2;

/** Shortest signed angle delta from a to b, in (-PI, PI]. */
export const angleDelta = (a: number, b: number): number => {
  let d = (b - a) % TWO_PI;
  if (d < -Math.PI) d += TWO_PI;
  if (d > Math.PI) d -= TWO_PI;
  return d;
};
